// services/authRetryExemptions.ts
//
// 403s that are answers, not lost sessions: the server has authenticated the
// caller and refused on plan grounds (and 401s from credential checks, below). Sessions are cookies (ORA-987), so the
// axios client never refreshes; it signs the tab out only on a 401. An admin
// step-up request has its own prompt-and-replay path, and an admin without
// two-factor authentication is sent to enrol.

/** Body codes that mean "authenticated, but not on a plan that allows this". */
const PLAN_FORBIDDEN_CODES = new Set([
  // Audio Overviews (ORA-735): free callers on create, retry, audio, progress.
  'AUDIO_OVERVIEW_UPGRADE_REQUIRED',
]);

/**
 * Admin passkey step-up (ORA-864): the caller is authenticated, but the admin
 * call needs a fresh passkey verification. The axios client prompts for one and
 * replays instead of refreshing the auth token.
 */
export const ADMIN_STEP_UP_REQUIRED_CODE = 'ADMIN_STEP_UP_REQUIRED';

/** The body code of a 403, or null for anything else. */
const forbiddenCode = (error: unknown): string | null => {
  const response = (
    error as { response?: { status?: unknown; data?: unknown } } | null
  )?.response;

  if (response?.status !== 403) return null;

  const code = (response.data as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : null;
};

/** True for a 403 asking for an admin passkey step-up (see above). */
export const isAdminStepUpRequired = (error: unknown): boolean =>
  forbiddenCode(error) === ADMIN_STEP_UP_REQUIRED_CODE;

/**
 * Routes whose 401 can answer a credential check, not a lost session
 * (ORA-987), each with the body `error` / `code` values that mean so (backend
 * ORA-986): a wrong current password on `changeEmail`, a bad reset token, a
 * wrong password on a login retried while signed in, a rejected admin passkey
 * assertion, and the bulk mapping-job rerun, which answers 401 to a signed-in
 * non-admin. Any other 401 body on these routes (`Invalid or missing token`,
 * `Missing Authorization header`, `A session cookie is required`, ...) is a
 * lost session. (`changePassword` answers a wrong password with a 200.) Paths
 * are relative to the API base, without a leading slash; a `:slug` segment
 * stands for any one segment (see `credentialRouteKey`). A wrong self-assessment
 * password (ORA-993) is a 401 too.
 */
export const CREDENTIAL_CHECK_FAILURES: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map([
  ['auth/changeEmail', new Set(['Password is incorrect'])],
  ['auth/resetPassword', new Set(['Invalid reset token'])],
  [
    'admin/passkeys/authenticate/verify',
    new Set(['PASSKEY_VERIFICATION_FAILED']),
  ],
  ['ai/jobs/rerun-lectures', new Set(['User authentication required'])],
  [
    'api/v5/self-assessments/:slug/verify-password',
    new Set(['Incorrect password.']),
  ],
]);

/** Paths whose one variable segment is folded into `:slug` for the lookup. */
const SLUG_ROUTES: ReadonlyArray<[RegExp, string]> = [
  [
    /^api\/v5\/self-assessments\/[^/]+\/verify-password$/,
    'api/v5/self-assessments/:slug/verify-password',
  ],
];

/** The `CREDENTIAL_CHECK_FAILURES` key for a request path. */
const credentialRouteKey = (path: string): string =>
  SLUG_ROUTES.find(([pattern]) => pattern.test(path))?.[1] ?? path;

/** The request path of an axios error (`/auth/x?y` → `auth/x`). */
const requestPath = (error: unknown): string | null => {
  const url = (error as { config?: { url?: unknown } } | null)?.config?.url;
  if (typeof url !== 'string') return null;
  try {
    return new URL(url, 'http://relative.invalid').pathname
      .replace(/^\/+/, '')
      .replace(/\/+$/, '');
  } catch {
    return null;
  }
};

/** True for a 401 from a route that checks credentials whose body names
 * the failed check (see above). */
export const isCredentialCheckFailure = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: unknown; data?: unknown } } | null
  )?.response;

  if (response?.status !== 401) return false;
  const path = requestPath(error);

  const failures =
    path === null
      ? undefined
      : CREDENTIAL_CHECK_FAILURES.get(credentialRouteKey(path));

  if (!failures) return false;

  const body = response.data as
    { error?: unknown; code?: unknown } | null | undefined;

  return [body?.error, body?.code].some(
    (value) => typeof value === 'string' && failures.has(value)
  );
};

/** True for an answer from a live session: a 403 plan refusal or step-up
 * request, or a 401 from a credential check. */
export const isExemptFromAuthRetry = (error: unknown): boolean => {
  if (isCredentialCheckFailure(error)) return true;
  const code = forbiddenCode(error);
  return (
    code !== null &&
    (PLAN_FORBIDDEN_CODES.has(code) || code === ADMIN_STEP_UP_REQUIRED_CODE)
  );
};

/**
 * An admin account without two-factor authentication (ORA-987): the backend
 * refuses admin routes until it is enrolled. The axios client sends the admin
 * to the Security settings instead of treating it as a lost session.
 */
export const TWO_FACTOR_SETUP_REQUIRED_CODE = 'TWO_FACTOR_SETUP_REQUIRED';

export const isTwoFactorSetupRequired = (error: unknown): boolean =>
  forbiddenCode(error) === TWO_FACTOR_SETUP_REQUIRED_CODE;
